import {
  blockerLabel,
  capacityDashboard,
  healthCount,
  priorityLabel,
  scoreTone
} from "./capacity";
import type { ProviderObservabilityView } from "./types";

const number = new Intl.NumberFormat("vi-VN");
const time = new Intl.DateTimeFormat("vi-VN", {
  timeZone: "Asia/Bangkok",
  dateStyle: "medium",
  timeStyle: "short"
});

function StatePill({ value }: { value: string }) {
  const tone =
    value === "HEALTHY" || value === "PASS"
      ? "good"
      : value === "DEGRADED" || value === "STALE" || value === "INSUFFICIENT"
        ? "warn"
        : "bad";
  return <span className={`pill ${tone}`}>{value.replaceAll("_", " ")}</span>;
}

function Score({ label, value, threshold }: { label: string; value: number; threshold: number }) {
  const tone = scoreTone(value);
  return (
    <div className="capacity-score">
      <div>
        <span>{label}</span>
        <strong className={tone}>{value.toFixed(1)}</strong>
      </div>
      <div className="capacity-score-track" aria-label={`${label}: ${value}/100`}>
        <span className={tone} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
        <i style={{ left: `${threshold}%` }} title={`Ngưỡng ${threshold}`} />
      </div>
      <small>Ngưỡng tối thiểu {threshold}</small>
    </div>
  );
}

function ProviderCard({ provider }: { provider: ProviderObservabilityView }) {
  return (
    <article className="capacity-provider-card">
      <header>
        <div>
          <span className="capacity-tier">{priorityLabel(provider.priority_tier)}</span>
          <h3>{provider.display_name}</h3>
          <p>
            {provider.provider_id} / {provider.model_id}
          </p>
        </div>
        <StatePill value={provider.health.state} />
      </header>

      <div className="capacity-score-grid">
        <Score
          label="Benchmark"
          value={provider.benchmark.score}
          threshold={provider.eligibility.minimum_quality_score}
        />
        <Score
          label="Health"
          value={provider.health.score}
          threshold={provider.eligibility.minimum_health_score}
        />
      </div>

      <dl className="capacity-facts">
        <div>
          <dt>Benchmark</dt>
          <dd>
            <StatePill value={provider.benchmark.state} /> · {provider.benchmark.sample_count} mẫu
          </dd>
        </div>
        <div>
          <dt>Latency p95</dt>
          <dd>{number.format(provider.health.latency_p95_ms)} ms</dd>
        </div>
        <div>
          <dt>Circuit / quota</dt>
          <dd>
            {provider.health.circuit_state} / {provider.health.quota_state}
          </dd>
        </div>
        <div>
          <dt>Context tối đa</dt>
          <dd>{number.format(provider.maximum_context_tokens)} tokens</dd>
        </div>
      </dl>

      <div className="capacity-tags" aria-label="Capabilities">
        {provider.capabilities.map((capability) => (
          <span key={capability}>{capability.replaceAll("_", " ")}</span>
        ))}
      </div>

      <div className="capacity-blockers">
        <strong>Không được tự động route</strong>
        <ul>
          {provider.eligibility.blockers.map((blocker) => (
            <li key={blocker}>{blockerLabel(blocker)}</li>
          ))}
        </ul>
      </div>

      <footer>
        <span>Fixture: {provider.benchmark.input_scope}</span>
        <span>Evidence {provider.benchmark.evidence_digest.slice(0, 18)}…</span>
      </footer>
    </article>
  );
}

export default function CapacityObservability() {
  const dashboard = capacityDashboard;
  return (
    <section className="capacity-shell">
      <header className="capacity-hero">
        <div>
          <span className="eyebrow">Offline capacity lab</span>
          <h1>Sức mạnh & sức khỏe nhà cung cấp</h1>
          <p>
            Bản xem trước chỉ đọc từ dữ liệu synthetic. Không provider nào trong màn hình này
            được phép nhận tác vụ thật.
          </p>
        </div>
        <span className="capacity-offline-badge">OFFLINE FIXTURE · FLAG OFF BY DEFAULT</span>
      </header>

      <div className="capacity-summary">
        <article>
          <span>Provider / model</span>
          <strong>{dashboard.summary.provider_model_count}</strong>
          <small>Đều là identity synthetic</small>
        </article>
        <article>
          <span>Đủ điều kiện route</span>
          <strong>{dashboard.summary.eligible_count}</strong>
          <small>Fail-closed cho mọi card</small>
        </article>
        <article>
          <span>Healthy / degraded</span>
          <strong>
            {healthCount(dashboard, "HEALTHY")} / {healthCount(dashboard, "DEGRADED")}
          </strong>
          <small>Chỉ là trạng thái fixture</small>
        </article>
        <article>
          <span>Snapshot</span>
          <strong className="capacity-time">{time.format(new Date(dashboard.generated_at))}</strong>
          <small>Không tự động refresh</small>
        </article>
      </div>

      <div className="capacity-safety-receipt">
        <strong>Safety receipt</strong>
        <span>Provider calls: {dashboard.safety.provider_calls}</span>
        <span>Spend: ${dashboard.safety.provider_spend_usd.toFixed(2)}</span>
        <span>Secret access: NO</span>
        <span>Raw prompt/customer data: NO</span>
      </div>

      <div className="capacity-provider-grid">
        {dashboard.providers.map((provider) => (
          <ProviderCard key={`${provider.provider_id}/${provider.model_id}`} provider={provider} />
        ))}
      </div>
    </section>
  );
}
